import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense, useState } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

// Context
import { AuthProvider } from "./contexts/AuthContext";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UserProvider } from "./contexts/UserContext";

// Layout - Critical components loaded immediately
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

// Home page loaded immediately for fast LCP on mobile
import Home from "./pages/Home";

// Lazy load all other pages for code splitting
const History = lazy(() => import("./pages/History"));
const Varieties = lazy(() => import("./pages/Varieties"));
const VarietyDetail = lazy(() => import("./pages/VarietyDetail"));
const Breeders = lazy(() => import("./pages/Breeders"));
const BreederDetail = lazy(() => import("./pages/BreederDetail"));
const Contests = lazy(() => import("./pages/Contests"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Partners = lazy(() => import("./pages/Partners"));
const Contact = lazy(() => import("./pages/Contact"));
const Admin = lazy(() => import("./pages/Admin"));
const Lexicon = lazy(() => import("./pages/Lexicon"));
const Login = lazy(() => import("./pages/Login"));
const Quiz = lazy(() => import("./pages/Quiz"));
const CommunityGallery = lazy(() => import("./pages/CommunityGallery"));
const KoiCare = lazy(() => import("./pages/KoiCare"));
const KoiFood = lazy(() => import("./pages/KoiFood"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Favorites = lazy(() => import("./pages/Favorites"));
const PondSimulator = lazy(() => import("./pages/PondSimulator"));
const PondCalculator = lazy(() => import("./pages/PondCalculator"));
const PondTools = lazy(() => import("./pages/PondTools"));
const SEODashboard = lazy(() => import("./pages/SEODashboard"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GoogleAuthCallback = lazy(() => import("./pages/GoogleAuthCallback"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Account = lazy(() => import("./pages/Account"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));

// Lazy load non-critical UI components
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
const KoiChatbot = lazy(() => import("./components/KoiChatbot"));
const AdminBadge = lazy(() => import("./components/EditableComponents").then(m => ({ default: m.AdminBadge })));

// Cookie consent - loaded with slight delay
const CookieConsent = lazy(() => import("./components/CookieConsent"));

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#D32F2F] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Deferred components loader - loads non-critical components after page is interactive
function DeferredComponents() {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    // Wait for the page to be interactive before loading these
    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  
  if (!show) return null;
  
  return (
    <Suspense fallback={null}>
      <WhatsAppButton />
      <KoiChatbot />
      <AdminBadge />
      <CookieConsent />
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
    <AuthProvider>
    <UserProvider>
      <FavoritesProvider>
        <div className="App min-h-screen flex flex-col washi-texture dark:bg-gray-900">
          <BrowserRouter>
          <ScrollToTop />
          <Navbar />
          <main className="flex-1">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
                <Route path="/histoire" element={<History />} />
                <Route path="/varietes" element={<Varieties />} />
                <Route path="/varietes/:id" element={<VarietyDetail />} />
                <Route path="/eleveurs" element={<Breeders />} />
                <Route path="/eleveurs/:id" element={<BreederDetail />} />
                <Route path="/concours" element={<Contests />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/partenaires" element={<Partners />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/lexique" element={<Lexicon />} />
                <Route path="/login" element={<Login />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/galerie" element={<CommunityGallery />} />
                <Route path="/soins" element={<KoiCare />} />
                <Route path="/alimentation" element={<KoiFood />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/favoris" element={<Favorites />} />
                <Route path="/outils-bassin" element={<PondTools />} />
                <Route path="/simulateur" element={<PondSimulator />} />
                <Route path="/calculateur" element={<PondCalculator />} />
                <Route path="/admin/seo" element={<SEODashboard />} />
                <Route path="/compte" element={<Account />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                {/* Catch-all route for 404 - must be last */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <DeferredComponents />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
      </FavoritesProvider>
    </UserProvider>
    </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
